import { notifyFeedbackActionCompleted } from '../../utils/feedbackEvents';
import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { useT } from '../../i18n/LanguageContext';
import service from '../../services/resourceCenterService';
import SizeCombinationPreview from './SizeCombinationPreview';
import { captureSession, isSessionCurrent } from '../../utils/authSession';
import { SizeExclusions, useSizingPreferences } from './SizePreferences';
import {
  canonicalSize,
  dimensionsArea,
  sizeLabel,
  validAreaResult,
  validRequestedArea,
} from '../../features/sizeFinder/sizingContract';

export { dimensionsArea, validAreaResult } from '../../features/sizeFinder/sizingContract';
export default function ProductSizing({ enabled, distributorId, readOnly = false }) {
  const t = useT();
  const scopeArgs = useMemo(() => (distributorId ? [distributorId] : []), [distributorId]);
  const state = useSizingPreferences(enabled, distributorId, readOnly);
  const { catalog } = state;
  const [groupId, setGroupId] = useState('');
  const [area, setArea] = useState('');
  const [inputMode, setInputMode] = useState('area');
  const [length, setLength] = useState('');
  const [width, setWidth] = useState('');
  const requestedArea = inputMode === 'area' ? area : dimensionsArea(length, width);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const sequence = useRef(0);
  const alive = useRef(true);
  const pending = useRef(false);
  const session = useRef(captureSession());
  const current = useCallback(() => alive.current && isSessionCurrent(session.current), []);
  const invalidatePending = useCallback(() => {
    sequence.current++;
  }, []);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      invalidatePending();
    };
  }, [invalidatePending]);
  useEffect(() => {
    sequence.current++;
    pending.current = false;
    setResult(null);
    setBusy(false);
    setGroupId('');
  }, [catalog, enabled]);
  const change = (setter) => (value) => {
    sequence.current++;
    pending.current = false;
    setResult(null);
    setError('');
    setBusy(false);
    setter(value);
  };
  const evaluate = async (e) => {
    e.preventDefault();
    if (pending.current || !current()) return;
    if (
      !enabled ||
      !catalog ||
      !state.preferences ||
      !groupId ||
      !(inputMode === 'area' ? validRequestedArea(area) : requestedArea)
    ) {
      setError(
        t(
          inputMode === 'dimensions'
            ? 'Enter length and width greater than 0 and up to 100 cm, with no more than two decimal places.'
            : 'Enter an area greater than 0 and up to 10,000 cm², with no more than two decimal places.'
        )
      );
      return;
    }
    const request = ++sequence.current;
    pending.current = true;
    setBusy(true);
    setResult(null);
    setError('');
    try {
      const next = await service.evaluateArea(
        {
          measurementMode: 'product_area',
          sizingGroupId: groupId,
          areaCm2: requestedArea,
          catalogFingerprint: catalog.catalogFingerprint,
          excludedSizeTokens: state.excluded,
        },
        ...scopeArgs
      );
      if (
        !validAreaResult(
          next,
          catalog.groups.find((item) => item.sizingGroupId === groupId),
          requestedArea,
          state.excluded,
          catalog.catalogFingerprint
        )
      )
        throw new Error('Area contract unavailable');
      if (current() && request === sequence.current) {
        setResult(next);
        if (['match', 'no_fit'].includes(next.status)) notifyFeedbackActionCompleted();
      }
    } catch (failure) {
      if (current() && request === sequence.current) {
        if (failure?.response?.status === 409) {
          setError(t('Available sizes changed. Review the product and try again.'));
          setGroupId('');
          state.load();
        } else setError(t('Could not check the size. Please try again.'));
      }
    } finally {
      if (current() && request === sequence.current) {
        pending.current = false;
        setBusy(false);
      }
    }
  };
  const pieces = result?.status === 'match' ? result.pieces : null;
  const group = catalog?.groups.find((item) => item.sizingGroupId === groupId);
  return (
    <section className="rc-sizing" aria-label={t('Size Finder')}>
      <form className="rc-sizing-inputs" onSubmit={evaluate} noValidate>
        <h2>{t('Find your sizes')}</h2>
        <p className="rc-muted">
          {t('Enter an area or length and width. We compare sizes within your selected product.')}
        </p>
        {state.sessionEnded ? (
          <p role="status">
            {t('Your session changed. Sign in again to load your size defaults.')}
          </p>
        ) : !enabled ? (
          <p role="status">
            {t('Size Finder is not available yet. You can still browse documents.')}
          </p>
        ) : state.loadError ? (
          <div role="alert">
            <p>{t('Could not load products and your saved size defaults. Please retry.')}</p>
            <button type="button" onClick={state.load}>
              {t('Retry')}
            </button>
          </div>
        ) : !catalog ? (
          <p role="status">{t('Loading products…')}</p>
        ) : (
          <>
            <label>
              {t('Primary product')}
              <select value={groupId} onChange={(e) => change(setGroupId)(e.target.value)}>
                <option value="">{t('Select a product')}</option>
                {catalog.groups.map((item) => (
                  <option key={item.sizingGroupId} value={item.sizingGroupId}>
                    {item.label}
                  </option>
                ))}
              </select>
            </label>
            {!catalog.groups.length && (
              <p className="rc-callout">
                {t(
                  'Verified sizing rules are not available yet. Product instructions remain available in Documents.'
                )}
              </p>
            )}
            <div className="rc-measurement-mode" role="group" aria-label={t('Measurement entry')}>
              <button
                type="button"
                aria-pressed={inputMode === 'area'}
                onClick={() => change(setInputMode)('area')}
              >
                {t('Area needed')}
              </button>
              <button
                type="button"
                aria-pressed={inputMode === 'dimensions'}
                onClick={() => change(setInputMode)('dimensions')}
              >
                {t('Length & width')}
              </button>
            </div>
            {inputMode === 'area' ? (
              <label>
                {t('Area needed (cm²)')}
                <input
                  inputMode="decimal"
                  autoComplete="off"
                  value={area}
                  onChange={(e) => change(setArea)(e.target.value)}
                  maxLength={8}
                  placeholder="25"
                />
              </label>
            ) : (
              <>
                <div className="rc-measurement-dimensions">
                  <label>
                    {t('Length (cm)')}
                    <input
                      inputMode="decimal"
                      autoComplete="off"
                      value={length}
                      onChange={(e) => change(setLength)(e.target.value)}
                      maxLength={6}
                      placeholder="5"
                    />
                  </label>
                  <label>
                    {t('Width (cm)')}
                    <input
                      inputMode="decimal"
                      autoComplete="off"
                      value={width}
                      onChange={(e) => change(setWidth)(e.target.value)}
                      maxLength={6}
                      placeholder="5"
                    />
                  </label>
                </div>
                <p className="rc-muted">
                  {requestedArea
                    ? t('Calculated area: {area} cm²', { area: requestedArea })
                    : t('Enter both measurements to calculate the area.')}
                </p>
                <p className="rc-muted">
                  {t(
                    'Length × width estimates a rectangular area; it does not verify sheet placement or wound shape.'
                  )}
                </p>
              </>
            )}
            <SizeExclusions
              sizes={(group?.products || []).map(canonicalSize)}
              state={{
                ...state,
                load: () => {
                  change(setGroupId)('');
                  state.load();
                },
              }}
              onChange={change(state.change)}
            />
            {error && (
              <p className="rc-error" role="alert">
                {error}
              </p>
            )}
            <button
              className="rc-primary"
              type="submit"
              disabled={busy || state.saving || !groupId}
            >
              {t(busy ? 'Checking size…' : 'Find size')}
            </button>
          </>
        )}
      </form>
      <div className="rc-sizing-result" aria-live="polite" aria-busy={busy}>
        {pieces ? (
          <>
            <span className="rc-eyebrow">{t('Suggested combination')}</span>
            <h2>
              {t(
                result.pieceCount === 1
                  ? '{count} piece · {area} cm² total'
                  : '{count} pieces · {area} cm² total',
                { count: result.pieceCount, area: result.totalAreaCm2 }
              )}
            </h2>
            <p className="rc-muted">
              {t('Area requested: {area} cm² · Excess: {excess} cm²', {
                area: result.requestedAreaCm2,
                excess: result.excessAreaCm2,
              })}
            </p>
            <SizeCombinationPreview
              result={result}
              length={inputMode === 'dimensions' ? length : null}
              width={inputMode === 'dimensions' ? width : null}
            />
            <div className="rc-piece-list">
              {pieces.map((piece, index) => {
                const source = /^https:\/\//i.test(piece.sourceUrl || '') ? piece.sourceUrl : null;
                return (
                  <article
                    className="rc-piece"
                    key={`${piece.productId || piece.sizeToken}-${index}`}
                  >
                    <div>
                      <strong>
                        ({piece.quantity}) {sizeLabel(piece.sizeToken)}
                      </strong>
                      <p className="rc-muted">{piece.name}</p>
                      <p className="rc-muted">
                        {t('{area} cm² per piece', { area: piece.areaCm2 })}
                      </p>
                      {source && (
                        <a href={source} target="_blank" rel="noopener noreferrer">
                          {t('View size source')} &#8599;
                        </a>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          </>
        ) : (
          <>
            <span className="material-symbols-outlined rc-size-icon" aria-hidden="true">
              square_foot
            </span>
            <h2>
              {t(
                busy
                  ? 'Checking your area…'
                  : result?.status === 'no_fit'
                    ? 'No eligible size combination'
                    : result?.status === 'rule_unavailable'
                      ? 'Sizing is unavailable for this product'
                      : 'Your size combination appears here'
              )}
            </h2>
            <p>
              {t(
                result
                  ? 'Try allowing more sizes or review the product instructions. We will not substitute another product.'
                  : 'Select a product and enter an area or length and width.'
              )}
            </p>
          </>
        )}
        <p className="rc-sizing-disclaimer">
          {t(
            'Product area comparison only. The layout is illustrative. Area does not guarantee physical coverage. Clinicians determine trimming, placement and clinical suitability. Follow product instructions.'
          )}
        </p>
      </div>
    </section>
  );
}
